/**
 * Read back a chat turn whose stream was cut off.
 *
 * iOS closes a backgrounded app's connection within seconds, which used to lose
 * the answer outright. The backend now finishes every turn whether or not the
 * client stays connected and saves it under the `turn_id` the `start` frame
 * carried, so a client that lost the stream polls `GET /chat/turn/{turn_id}`
 * until the turn settles. Identical in the mobile app (`src/services/turnRecovery.ts`).
 */

export interface TurnResult {
  status: 'complete' | 'pending' | 'failed';
  text?: string | null;
  sources?: any[];
  agent?: string | null;
}

export type FetchTurn = (turnId: string, conversationId: string) => Promise<TurnResult>;

/** Matches the backend's `DETACHED_TURN_MAX_S`: past it the turn was cancelled. */
export const TURN_RECOVERY_MAX_WAIT_MS = 600_000;
export const TURN_RECOVERY_INTERVAL_MS = 3_000;

export function turnPath(turnId: string, conversationId: string): string {
  return `/chat/turn/${encodeURIComponent(turnId)}?conversation_id=${encodeURIComponent(conversationId)}`;
}

function wait(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) return resolve();
    const timer = setTimeout(done, ms);
    function done() {
      clearTimeout(timer);
      signal?.removeEventListener('abort', done);
      resolve();
    }
    signal?.addEventListener('abort', done);
  });
}

/**
 * Poll until the turn is `complete` or `failed`, the wait runs out, or `signal`
 * aborts. The first check is immediate: a turn that finished while the app was
 * away is usually already saved.
 *
 * A failed request counts as `pending` rather than ending the wait, since the
 * network is often still waking up when the app returns to the foreground.
 *
 * @returns The finished answer, or `null` when there is none to show.
 */
export async function waitForTurnResult(options: {
  turnId: string;
  conversationId: string;
  fetchTurn: FetchTurn;
  signal?: AbortSignal;
  intervalMs?: number;
  maxWaitMs?: number;
  now?: () => number;
}): Promise<(TurnResult & { status: 'complete' }) | null> {
  const {
    turnId,
    conversationId,
    fetchTurn,
    signal,
    intervalMs = TURN_RECOVERY_INTERVAL_MS,
    maxWaitMs = TURN_RECOVERY_MAX_WAIT_MS,
    now = Date.now,
  } = options;
  const deadline = now() + maxWaitMs;

  while (!signal?.aborted) {
    try {
      const result = await fetchTurn(turnId, conversationId);
      if (result.status === 'complete' && result.text) {
        return result as TurnResult & { status: 'complete' };
      }
      if (result.status === 'failed') return null;
    } catch {
      // Treated as still pending — see above.
    }
    if (now() + intervalMs > deadline) return null;
    await wait(intervalMs, signal);
  }
  return null;
}
